import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GameDetails, GameEvent } from '../domain/interfaces/game-details';
import { mapGameEvent } from '../domain/data-mappers/game-event-mapper';
import { sortGameEvents } from '../utils/games-utils';
import {
  addGameEvent,
  changeGameDetailsVideoUrl,
  deleteGameEvent,
  editGameEvent,
  editGameScore,
  fetchGameDetails,
  saveGameDetails,
} from './actions';

export type GameDetailsSliceState = {
  isLoading: boolean;
  isLoaded: boolean;
  eventsChanged: boolean;
  isSaved: boolean;
  data: GameDetails | null;
};

const initialState: GameDetailsSliceState = {
  isLoading: false,
  isLoaded: false,
  eventsChanged: false,
  isSaved: false,
  data: null,
};

function createFetchGameDetailsReducers(builder: ActionReducerMapBuilder<GameDetailsSliceState>) {
  builder.addCase(fetchGameDetails.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(fetchGameDetails.fulfilled, (state, action) => {
    state.isLoading = false;
    state.isLoaded = true;
    state.eventsChanged = false;
    state.data = action.payload;
  });

  builder.addCase(fetchGameDetails.rejected, (state) => {
    state.isLoading = false;
    state.isLoaded = true;
  });
}

function createAddGameEventReducers(builder: ActionReducerMapBuilder<GameDetailsSliceState>) {
  builder.addCase(addGameEvent, (state, action) => {
    if (!state.data) {
      return;
    }

    const { events: prevEvents } = state.data;
    const { data, periodSettings } = action.payload;

    let nextEventData = data;
    if (data.period === 0) {
      const lastPenaltySecond =
        prevEvents
          .slice()
          .reverse()
          .find((e) => e.period === data.period && e.teamId === data.teamId)?.seconds || 0;

      nextEventData = {
        ...nextEventData,
        seconds: lastPenaltySecond + 60,
      };
    }

    const events = sortGameEvents([...state.data.events, mapGameEvent(nextEventData, periodSettings)]);

    state.eventsChanged = true;
    state.data = {
      ...state.data,
      events,
    };
  });
}

function createEditGameEventReducers(builder: ActionReducerMapBuilder<GameDetailsSliceState>) {
  builder.addCase(editGameEvent, (state, action) => {
    const { data, periodSettings } = action.payload;

    if (!state.data || !data.id) {
      return;
    }

    const id = data.id!;
    const updatedEvents = state.data.events.map((event) => {
      if (event.id === id) {
        return mapGameEvent(data, periodSettings);
      }

      return event;
    });

    state.eventsChanged = true;
    state.data = {
      ...state.data,
      events: sortGameEvents(updatedEvents),
    };
  });
}

function createDeleteGameEventReducers(builder: ActionReducerMapBuilder<GameDetailsSliceState>) {
  builder.addCase(deleteGameEvent, (state, action) => {
    const id = action.payload;
    if (!state.data || !id) {
      return;
    }

    const updatedEvents = state.data.events.reduce((result: GameEvent[], event) => {
      if (event.id === id) {
        return result;
      }

      return [...result, event];
    }, []);

    state.eventsChanged = true;
    state.data = {
      ...state.data,
      events: sortGameEvents(updatedEvents),
    };
  });
}

function createEditGameScoreReducer(builder: ActionReducerMapBuilder<GameDetailsSliceState>) {
  builder.addCase(editGameScore, (state, action) => {
    if (!state.data) {
      return;
    }

    state.eventsChanged = true;
    state.data = {
      ...state.data,
      result: action.payload,
    };
  });
}

function createChangeGameDetailsVideoUrlReducer(builder: ActionReducerMapBuilder<GameDetailsSliceState>) {
  builder.addCase(changeGameDetailsVideoUrl, (state, action) => {
    const videoUrl = action.payload;

    if (!state.data) {
      return;
    }

    state.eventsChanged = true;
    state.data = {
      ...state.data,
      videoUrls: videoUrl ? [videoUrl] : [],
    };
  });
}

function createSaveGameDetailsReducer(builder: ActionReducerMapBuilder<GameDetailsSliceState>) {
  builder.addCase(saveGameDetails.fulfilled, (state, { payload }) => {
    state.isSaved = true;
    state.eventsChanged = false;

    if (state.data) {
      state.data = {
        ...state.data,
        participants: payload.details.participants,
      };
    }
  });

  builder.addCase(saveGameDetails.rejected, (state) => {
    state.isSaved = false;
  });
}

export const gameDetailsSlice = createSlice({
  name: 'games/details',
  initialState,
  reducers: {
    setMomentUrl: (state, action: PayloadAction<{ eventId: number; url: string }>) => {
      if (!state.data) {
        return;
      }

      state.eventsChanged = true;
      state.data = {
        ...state.data,
        events: state.data.events.map((event) => {
          if (event.id === action.payload.eventId) {
            return {
              ...event,
              videoUrl: action.payload.url,
            };
          }

          return event;
        }),
      };
    },
    setTimestampUrl: (state, action: PayloadAction<{ key: string; url: string }>) => {
      if (!state.data) {
        return;
      }

      const { key, url } = action.payload;

      // Сделать получение секунды из url
      const second = 0;
      const data = { url, second };

      if (state.data.timestamps) {
        state.data.timestamps[key] = data;
      } else {
        state.data.timestamps = {
          [key]: data,
        };
      }
    },
  },
  extraReducers: (builder) => {
    createFetchGameDetailsReducers(builder);
    createAddGameEventReducers(builder);
    createEditGameEventReducers(builder);
    createDeleteGameEventReducers(builder);
    createSaveGameDetailsReducer(builder);
    createEditGameScoreReducer(builder);
    createChangeGameDetailsVideoUrlReducer(builder);
  },
});

export const { setMomentUrl, setTimestampUrl } = gameDetailsSlice.actions;
